import React, {
  useState,
  memo,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { useRouter } from 'next/router';
import {
  SideNavCategory,
  SideNavColorSchemeProps,
  SideNavItemSelectEvent,
} from '..';
import { formatSubCategoryName, getHeadItems, noop } from '../../../util';
import * as Styled from './styled';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { buildReportFieldDraggableId } from '../../../features/create-report/rule-builder/rule-builder-dragdrop-context/helpers';
import { StandardReport, StringOrComponent } from '../../../models';
import { useReportContext } from '../../../context/ReportContext';
import { useIsFrs102Enabled } from '../../../util/hooks/useIsFrs102Enabled';
import { replaceIfrsLabels } from '../../../util/frs102Helper';

interface SubCategorySectionProps extends SideNavColorSchemeProps {
  subCategory: SideNavCategory;
  isSearching: boolean;
  maxItemCount?: number;
  onItemSelect?: (e: SideNavItemSelectEvent) => void;
  onItemDoubleClick?: () => void;
  selectedItem?: SideNavCategory;
  isCollapsible?: boolean;
  isDraggable?: boolean;
  dropableId?: string;
  subItemDisplayBuilder?: (category: SideNavCategory) => StringOrComponent;
  subItemListBuilder?: (
    category: SideNavCategory,
    defaultItemComponents?: StringOrComponent[],
  ) => StringOrComponent;
  handleAutoSelectUsed?: (wasUsed: boolean) => void;
}

function SubCategorySection({
  subCategory,
  isSearching,
  maxItemCount,
  onItemSelect,
  selectedItem,
  colorScheme,
  onItemDoubleClick,
  isCollapsible,
  isDraggable,
  dropableId,
  subItemDisplayBuilder = (category: SideNavCategory): StringOrComponent =>
    category.name,
  subItemListBuilder = (
    category: SideNavCategory,
    defaultItemComponents?: StringOrComponent[],
  ): StringOrComponent => <>{defaultItemComponents}</>,
  handleAutoSelectUsed,
}: SubCategorySectionProps): JSX.Element {
  const router = useRouter();
  const { expandSavedReports } = router.query;
  const subItems = subCategory.items;
  const [isCollapsed, setIsCollapsed] = useState<boolean>(isCollapsible);
  const [repositionKey, setRepositionKey] = useState<string>(null);
  const isDisplaySeeAllButton = isCollapsible && subItems.length > maxItemCount;
  const displayedItemCount = isCollapsed
    ? Math.min(maxItemCount, subItems.length)
    : subItems.length;
  const scrollToItemRef = useRef(null);
  const { autoSelectScrollTo, setAutoSelectScrollTo } = useReportContext();
  const isFrs102Enabled = useIsFrs102Enabled();

  useEffect(() => {
    if (expandSavedReports === 'true' && subCategory.name === 'Saved') {
      setIsCollapsed(false);
    }
  }, [expandSavedReports, subCategory]);

  useEffect(() => {
    if (!isCollapsible) {
      setIsCollapsed(false);
    }
  }, [isCollapsible]);

  // useEffect for when the selected item is hidden by the collapsed section, open the section
  useEffect(() => {
    if (autoSelectScrollTo) {
      if (repositionKey === null) {
        // only run this useEffect for the selectedItem's section
        if (selectedItem) {
          if (selectedItem.parent.name === subCategory.name) {
            // only worry about sections that are currently collapsed
            if (isCollapsed) {
              const indexOfSelectedItem = subItems
                .map((i) => i.key)
                .indexOf(selectedItem.key);

              // if the selected item is greater than displayedItemCount in the subItems array, expand the section
              if (indexOfSelectedItem > displayedItemCount) {
                setIsCollapsed(false);
                setRepositionKey(selectedItem.key);
              }
            }
          }
        }
      }
    }
  }, [
    selectedItem,
    subItems,
    subCategory.name,
    isCollapsed,
    displayedItemCount,
    repositionKey,
    autoSelectScrollTo,
  ]);

  useEffect(() => {
    if (autoSelectScrollTo) {
      if (scrollToItemRef.current && selectedItem && !isCollapsed) {
        if (repositionKey === selectedItem?.key) {
          setAutoSelectScrollTo(false);
          scrollToItemRef.current?.scrollIntoView();
        }
      }
    }
  }, [
    repositionKey,
    selectedItem,
    isCollapsed,
    autoSelectScrollTo,
    handleAutoSelectUsed,
    setAutoSelectScrollTo,
  ]);

  const displayedItems = useMemo(
    () => getHeadItems<SideNavCategory>(subItems, displayedItemCount),
    [subItems, displayedItemCount],
  );

  const collapseButtonClickHandler = useCallback((): void => {
    setIsCollapsed(!isCollapsed);
  }, [isCollapsed]);

  const subItemComponents = useMemo(
    () =>
      displayedItems.map((subItem, index) => {
        const isActive = selectedItem && subItem.key === selectedItem.key;
        const itemClickHandler = (e: React.MouseEvent): void => {
          if (!isActive) {
            onItemSelect({
              ...e,
              category: { ...subItem, parent: subCategory },
            });
          }
        };

        const draggableId = buildReportFieldDraggableId(
          dropableId,
          subItem.key,
        );
        return isDraggable ? (
          <Draggable key={subItem.key} draggableId={draggableId} index={index}>
            {(provided, snapshot): JSX.Element => (
              <>
                <Styled.SubCategoryItem
                  isDraggable={true}
                  isDragging={snapshot.isDragging}
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  colorScheme={colorScheme}
                  isActive={isActive}
                  onClick={itemClickHandler}
                  onDoubleClick={onItemDoubleClick}
                  className="subCategoryItem"
                  data-draggableid={draggableId}
                >
                  <>{subItemDisplayBuilder(subItem)}</>
                </Styled.SubCategoryItem>
                {snapshot.isDragging && (
                  <Styled.SubCategoryItemDraggingPlaceholder
                    colorScheme={colorScheme}
                    className="subCategoryItemDraggingPlaceholder"
                  >
                    <>{subItemDisplayBuilder(subItem)}</>
                  </Styled.SubCategoryItemDraggingPlaceholder>
                )}
              </>
            )}
          </Draggable>
        ) : (
          <Styled.SubCategoryItem
            key={subItem.key}
            ref={repositionKey === subItem.key ? scrollToItemRef : null}
            colorScheme={colorScheme}
            isActive={isActive}
            onClick={itemClickHandler}
            onDoubleClick={onItemDoubleClick}
          >
            <>{subItemDisplayBuilder(subItem)}</>
          </Styled.SubCategoryItem>
        );
      }),
    [
      displayedItems,
      selectedItem,
      dropableId,
      isDraggable,
      colorScheme,
      onItemDoubleClick,
      onItemSelect,
      subCategory,
      subItemDisplayBuilder,
      repositionKey,
    ],
  );

  const combinedDroppableId = `${dropableId}-${subCategory.key}`;

  let headerLabelOnSearch = subCategory.formattedSearchText;
  const firstSubItemTagExists = subItems && subItems.length > 0 && subItems[0].tag;
  let subItemExpectedHeadLabel = headerLabelOnSearch;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isStandardReport = (object: any) : object is StandardReport => {
    return 'category' in object && 'subCategory' in object;
  }

  if (firstSubItemTagExists && isStandardReport(subItems[0].tag)) {
    const subItemTag = subItems[0].tag as StandardReport;
    subItemExpectedHeadLabel = formatSubCategoryName(subItemTag.category, subItemTag.subCategory)
  }
  
  if (headerLabelOnSearch !== subItemExpectedHeadLabel) {
    headerLabelOnSearch = subItemExpectedHeadLabel;
  }

  const headerLabel = isSearching
    ? headerLabelOnSearch
    : subCategory.name;
  const displayHeaderLabel = replaceIfrsLabels(
    headerLabel,
    isFrs102Enabled,
  );

  return (
    <Styled.SubCategorySection colorScheme={colorScheme}>
      {(subCategory.name || isDisplaySeeAllButton) && (
        <Styled.SubCategoryHeaderBar>
          <Styled.SubCategoryHeaderLabel>
            {displayHeaderLabel}
          </Styled.SubCategoryHeaderLabel>
          {isDisplaySeeAllButton && (
            <Styled.CollapseButton onClick={collapseButtonClickHandler}>
              {isCollapsed ? `See All (${subItems.length})` : 'Close'}
            </Styled.CollapseButton>
          )}
        </Styled.SubCategoryHeaderBar>
      )}
      {isDraggable ? (
        <Droppable droppableId={combinedDroppableId} isDropDisabled={true}>
          {(provided): JSX.Element => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              data-droppableid={combinedDroppableId}
            >
              <>{subItemListBuilder(subCategory, subItemComponents)}</>
              {/* this empty div element is required to ensure hovered items have bottom margin  */}
              <div />
            </div>
          )}
        </Droppable>
      ) : (
        <>
          {subItemListBuilder(subCategory, subItemComponents)}
          {/* this empty div element is required to ensure hovered items have bottom margin  */}
          <div />
        </>
      )}
    </Styled.SubCategorySection>
  );
}

SubCategorySection.defaultProps = {
  maxItemCount: 5,
  isCollapsible: true,
  colorScheme: 'dark',
  onItemSelect: noop,
  isDraggable: false,
};

export default memo(SubCategorySection);
